import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, ChevronUp, ChevronDown, X, Plus, Search, FileText, ClipboardList, Pill, Stethoscope } from "lucide-react";
import { icdSearch, addDiagnosis } from "@/lib/noteClaim";

const INK = "#141412", INDIGO = "#5856D6", SUB = "#6C6C69", FAINT = "#9B9B97", HAIR = "#E9E9EC", PANEL = "#FBFBFA", AMBER = "#9A5A00";
const LETTERS = "ABCDEFGHIJKL".split("");

const IconBtn = ({ onClick, title, disabled, children, testid }) => (
  <button onClick={onClick} disabled={disabled} title={title} data-testid={testid}
    className="w-[26px] h-[26px] inline-flex items-center justify-center rounded-[7px] disabled:opacity-30 hover:bg-black/[.05]" style={{ color: SUB }}>{children}</button>
);

/* ---------- inline add-diagnosis search ---------- */
function AddDiagnosis({ noteId, dos, onAdded }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const abortRef = useRef(null);
  useEffect(() => {
    const term = q.trim();
    const minOk = /^[a-z]\d/i.test(term) ? term.length >= 3 : term.length >= 5 || /^[a-z]\d/i.test(term);
    if (!open) return undefined;
    if (term.length < 3 || (!/^[a-z]?\d/i.test(term) && term.length < 5)) { setRes(null); return undefined; }
    if (!minOk && term.length < 5) { setRes(null); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { if (abortRef.current) abortRef.current.abort(); abortRef.current = new AbortController();
        const d = await icdSearch(term, { dos, signal: abortRef.current.signal }); setRes(d); }
      catch (e) { if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setRes({ nodes: [] }); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open, dos]);

  const pick = async (node) => {
    setErr("");
    try { const nn = await addDiagnosis(noteId, node.code_dotted || node.code); onAdded(nn); setOpen(false); setQ(""); setRes(null); toast.success(`Added ${node.code_dotted || node.code}`); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not add that diagnosis."); }
  };

  if (!open) return <button onClick={() => setOpen(true)} className="text-[12px] font-semibold inline-flex items-center gap-1" style={{ color: INDIGO }} data-testid="er-add-dx"><Plus size={13} /> Add diagnosis</button>;
  return (
    <div className="mt-1" data-testid="er-add-dx-panel">
      <div className="flex items-center gap-1.5 rounded-[8px] px-2 py-1.5" style={{ border: `1px solid ${HAIR}`, background: "#fff" }}>
        <Search size={13} style={{ color: FAINT }} />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} placeholder="Code or description" className="flex-1 text-[12.5px] outline-none" data-testid="er-add-dx-input" />
        {loading ? <Loader2 size={13} className="animate-spin" style={{ color: FAINT }} /> : <button onClick={() => { setOpen(false); setQ(""); }}><X size={13} style={{ color: FAINT }} /></button>}
      </div>
      {err ? <div className="text-[11.5px] mt-1" style={{ color: "#C4302A" }} data-testid="er-add-dx-err">{err}</div> : null}
      {res?.nodes?.length ? (
        <div className="mt-1 max-h-[220px] overflow-y-auto rounded-[8px]" style={{ border: `1px solid ${HAIR}` }}>
          {res.nodes.map((n) => (
            <button key={n.code_dotted + n.code} disabled={!n.selectable} onClick={() => n.selectable && pick(n)}
              className="w-full flex items-start gap-2 px-2.5 py-1.5 text-left disabled:opacity-45 enabled:hover:bg-black/[.04]" data-testid={`er-dx-opt-${n.code_dotted}`}>
              <span className="text-[11.5px] font-bold tabular-nums flex-none" style={{ fontFamily: "monospace", color: n.selectable ? INK : FAINT }}>{n.code_dotted}</span>
              <span className="text-[12px] leading-snug" style={{ color: SUB }}>{n.description}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- right cards ---------- */
function Card({ icon: Ic, title, count, onCopy, children, testid }) {
  return (
    <div className="rounded-[11px] p-3.5" style={{ background: "#fff", border: `1px solid ${HAIR}` }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2">
        <Ic size={13} style={{ color: SUB }} />
        <span className="text-[11px] font-bold uppercase tracking-[.05em]" style={{ color: INK }}>{title}</span>
        {count != null ? <span className="text-[10.5px] font-bold tabular-nums" style={{ color: FAINT }}>{count}</span> : null}
        {onCopy ? <button onClick={onCopy} className="ml-auto" title="Copy"><Copy size={12} style={{ color: FAINT }} /></button> : null}
      </div>
      {children}
    </div>
  );
}

// Today's editable cards, unchanged — shown by "Edit codes" in the On the claim panel.
export function EditCards({ note, d, icd, cpt, rx, tov, tovList, letterOf, moveDx, removeDx, setDetail, onChanged, changeTov, copyDx, copyCpt, copyRx }) {
  return (
    <div className="flex flex-col gap-[10px] px-3 py-1.5" data-testid={`er-edit-cards-${note.id}`}>
              <Card icon={ClipboardList} title={`Box 21 · ${icd.length} of 12`} onCopy={icd.length ? copyDx : null} testid={`er-dx-${note.id}`}>
                {icd.length ? icd.map((c, i) => (
                  <div key={c.code + i} className="flex items-start gap-2 py-1">
                    <span className="text-[11px] font-extrabold w-[14px] flex-none" style={{ color: INK }}>{LETTERS[i]}</span>
                    <div className="min-w-0 flex-1">
                      {i === 0 ? <div className="text-[9px] font-bold mb-0.5" style={{ color: INDIGO }}>PRIMARY · BOX 21 A</div> : null}
                      <div className="flex items-start gap-1.5"><span className="text-[11.5px] font-semibold tabular-nums flex-none" style={{ fontFamily: "monospace", color: INK }}>{c.code}</span><span className="text-[11.5px] leading-snug" style={{ color: SUB }}>{c.description}</span></div>
                    </div>
                    <span className="flex items-center flex-none">
                      <IconBtn onClick={() => moveDx(i, -1)} disabled={i === 0} title="Move up" testid={`er-dx-up-${c.code}`}><ChevronUp size={13} /></IconBtn>
                      <IconBtn onClick={() => moveDx(i, 1)} disabled={i === icd.length - 1} title="Move down" testid={`er-dx-down-${c.code}`}><ChevronDown size={13} /></IconBtn>
                      <IconBtn onClick={() => removeDx(c.code)} disabled={icd.length <= 1} title="Remove" testid={`er-dx-rm-${c.code}`}><X size={13} /></IconBtn>
                    </span>
                  </div>
                )) : <div className="text-[12px]" style={{ color: FAINT }}>None coded.</div>}
                <div className="mt-1.5"><AddDiagnosis noteId={note.id} dos={d.date_of_service} onAdded={(nn) => { if (nn) setDetail(nn); onChanged?.(); }} /></div>
              </Card>

              <Card icon={Stethoscope} title={`CPT · ${cpt.length}`} onCopy={cpt.length ? copyCpt : null} testid={`er-cpt-${note.id}`}>
                {cpt.length ? cpt.map((c, i) => {
                  const ptrs = (c.dx || []).map((x) => letterOf(x.code));
                  return (
                    <div key={c.code + i} className="flex items-start gap-2 py-1">
                      <span className="text-[11.5px] font-semibold tabular-nums flex-none" style={{ fontFamily: "monospace", color: INK }}>{c.code}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11.5px] leading-snug" style={{ color: SUB }}>{c.description}</div>
                        {ptrs.length ? <div className="text-[10.5px] mt-0.5" style={{ color: FAINT }}>Points to {ptrs.map((p, k) => <span key={k} className="font-bold" style={{ color: INK }}>{p}{k < ptrs.length - 1 ? ", " : ""}</span>)}</div> : <div className="text-[10.5px] mt-0.5 font-semibold" style={{ color: AMBER }}>No diagnosis pointer</div>}
                      </div>
                      {(c.units || c.avg_paid?.amt) ? <span className="text-[11px] tabular-nums flex-none" style={{ color: FAINT }}>{c.units ? `×${c.units}` : ""}</span> : null}
                    </div>
                  );
                }) : <div className="text-[12px]" style={{ color: FAINT }}>None coded.</div>}
              </Card>

              {rx.length ? (
                <Card icon={Pill} title={`e-Rx queue · ${rx.length}`} onCopy={copyRx} testid={`er-rx-${note.id}`}>
                  <div className="space-y-2">
                    {rx.map((m, i) => (
                      <div key={m.id || i} className="rounded-[8px] px-2.5 py-2" style={{ background: PANEL, border: `1px solid ${HAIR}` }}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[12.5px] font-bold" style={{ color: INK }}>{m.drug_name}</span>
                          {m.strength ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-[5px]" style={{ background: "#fff", border: `1px solid ${HAIR}`, color: SUB }}>{m.strength}</span> : null}
                          {m.origin === "represcribe" ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FFF4E0", color: AMBER }}>Re-prescribe</span> : null}
                        </div>
                        {m.sig ? <div className="text-[12px] mt-1" style={{ color: SUB }}>{m.sig}</div> : null}
                        {(m.refills != null || m.days_supply) ? <div className="text-[11px] mt-0.5" style={{ color: FAINT }}>{[m.days_supply ? `${m.days_supply} days` : "", m.refills != null ? `${m.refills} refills` : ""].filter(Boolean).join(" · ")}</div> : null}
                      </div>
                    ))}
                  </div>
                </Card>
              ) : null}

              <Card icon={FileText} title="Visit type" testid={`er-tov-${note.id}`}>
                <select value={tov} onChange={(e) => changeTov(e.target.value)} className="w-full text-[12.5px] rounded-[8px] px-2 py-1.5 outline-none" style={{ border: `1px solid ${HAIR}`, background: "#fff", color: INK }} data-testid={`er-tov-select-${note.id}`}>
                  <option value="NOT SET">NOT SET</option>
                  {tovList.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {d.tov_reason ? <div className="text-[11px] mt-1.5" style={{ color: FAINT }}>{d.tov_reason}</div> : null}
              </Card>
    </div>
  );
}
